/**
 * Jepson Bioregion Hierarchy & Matching
 *
 * Implements the Jepson eFlora bioregion system used by California botanists.
 * Each species has a `bioregional_distributions` string like:
 *   "NW (exc NCo), CaRH, SNH, Teh, w CW (exc n CCo), TR, PR, GB"
 *
 * Ported from california-species-atlas/src/lib/jepson-regions.ts
 */

import * as turf from "@turf/turf";
import { JEPSON_DATA_ENABLED } from "utils/jepsonVisibility";

// ─── Jepson Region Hierarchy ───

export interface JepsonRegion {
  code: string;
  name: string;
  parent: string | null;
  children: string[];
  bounds: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  centroid: [number, number]; // [lng, lat]
}

export const JEPSON_REGIONS: Record<string, JepsonRegion> = {
  CA: {
    code: "CA",
    name: "California",
    parent: null,
    children: ["CA-FP", "GB", "D"],
    bounds: [-124.5, 32.5, -114.1, 42.0],
    centroid: [-119.5, 37.5],
  },
  "CA-FP": {
    code: "CA-FP",
    name: "California Floristic Province",
    parent: "CA",
    children: ["NW", "CaR", "SN", "GV", "CW", "SW"],
    bounds: [-124.5, 32.5, -117.5, 42.0],
    centroid: [-121.0, 37.5],
  },
  NW: {
    code: "NW",
    name: "Northwestern California",
    parent: "CA-FP",
    children: ["KR", "NCo", "NCoR"],
    bounds: [-124.5, 38.5, -122.0, 42.0],
    centroid: [-123.3, 40.5],
  },
  KR: {
    code: "KR",
    name: "Klamath Ranges",
    parent: "NW",
    children: [],
    bounds: [-124.0, 40.5, -122.0, 42.0],
    centroid: [-123.0, 41.3],
  },
  NCo: {
    code: "NCo",
    name: "North Coast",
    parent: "NW",
    children: [],
    bounds: [-124.5, 38.5, -123.0, 42.0],
    centroid: [-124.0, 40.0],
  },
  NCoR: {
    code: "NCoR",
    name: "North Coast Ranges",
    parent: "NW",
    children: ["NCoRH", "NCoRI", "NCoRO"],
    bounds: [-123.5, 38.5, -121.5, 40.5],
    centroid: [-122.5, 39.5],
  },
  NCoRH: {
    code: "NCoRH",
    name: "High North Coast Ranges",
    parent: "NCoR",
    children: [],
    bounds: [-123.5, 39.0, -122.0, 40.5],
    centroid: [-122.8, 39.8],
  },
  NCoRI: {
    code: "NCoRI",
    name: "Inner North Coast Ranges",
    parent: "NCoR",
    children: [],
    bounds: [-123.0, 38.5, -121.5, 40.0],
    centroid: [-122.2, 39.2],
  },
  NCoRO: {
    code: "NCoRO",
    name: "Outer North Coast Ranges",
    parent: "NCoR",
    children: [],
    bounds: [-123.5, 38.5, -122.5, 40.0],
    centroid: [-123.0, 39.2],
  },
  CaR: {
    code: "CaR",
    name: "Cascade Ranges",
    parent: "CA-FP",
    children: ["CaRF", "CaRH"],
    bounds: [-122.5, 40.0, -121.0, 42.0],
    centroid: [-121.8, 41.0],
  },
  CaRF: {
    code: "CaRF",
    name: "Cascade Range Foothills",
    parent: "CaR",
    children: [],
    bounds: [-122.5, 40.0, -121.5, 41.0],
    centroid: [-122.0, 40.5],
  },
  CaRH: {
    code: "CaRH",
    name: "High Cascade Range",
    parent: "CaR",
    children: [],
    bounds: [-122.0, 40.5, -121.0, 42.0],
    centroid: [-121.5, 41.3],
  },
  SN: {
    code: "SN",
    name: "Sierra Nevada",
    parent: "CA-FP",
    children: ["SNF", "SNH", "Teh"],
    bounds: [-121.5, 35.5, -118.5, 40.0],
    centroid: [-120.0, 37.8],
  },
  SNF: {
    code: "SNF",
    name: "Sierra Nevada Foothills",
    parent: "SN",
    children: ["nSNF", "cSNF", "sSNF"],
    bounds: [-121.5, 35.5, -119.5, 40.0],
    centroid: [-120.5, 37.8],
  },
  nSNF: {
    code: "nSNF",
    name: "Northern Sierra Nevada Foothills",
    parent: "SNF",
    children: [],
    bounds: [-121.5, 38.5, -120.0, 40.0],
    centroid: [-120.8, 39.2],
  },
  cSNF: {
    code: "cSNF",
    name: "Central Sierra Nevada Foothills",
    parent: "SNF",
    children: [],
    bounds: [-121.0, 37.0, -119.5, 38.5],
    centroid: [-120.2, 37.8],
  },
  sSNF: {
    code: "sSNF",
    name: "Southern Sierra Nevada Foothills",
    parent: "SNF",
    children: [],
    bounds: [-120.5, 35.5, -118.5, 37.0],
    centroid: [-119.5, 36.2],
  },
  SNH: {
    code: "SNH",
    name: "High Sierra Nevada",
    parent: "SN",
    children: ["nSNH", "cSNH", "sSNH"],
    bounds: [-120.5, 36.0, -118.5, 40.0],
    centroid: [-119.5, 38.0],
  },
  nSNH: {
    code: "nSNH",
    name: "Northern High Sierra Nevada",
    parent: "SNH",
    children: [],
    bounds: [-120.5, 38.5, -119.5, 40.0],
    centroid: [-120.0, 39.2],
  },
  cSNH: {
    code: "cSNH",
    name: "Central High Sierra Nevada",
    parent: "SNH",
    children: [],
    bounds: [-120.0, 37.0, -118.5, 38.5],
    centroid: [-119.2, 37.8],
  },
  sSNH: {
    code: "sSNH",
    name: "Southern High Sierra Nevada",
    parent: "SNH",
    children: [],
    bounds: [-119.5, 36.0, -118.0, 37.0],
    centroid: [-118.8, 36.5],
  },
  Teh: {
    code: "Teh",
    name: "Tehachapi Mountains Area",
    parent: "SN",
    children: [],
    bounds: [-119.5, 34.8, -118.0, 35.5],
    centroid: [-118.8, 35.1],
  },
  GV: {
    code: "GV",
    name: "Great Central Valley",
    parent: "CA-FP",
    children: ["ScV", "SnJV"],
    bounds: [-122.5, 34.8, -119.5, 40.5],
    centroid: [-121.0, 37.5],
  },
  ScV: {
    code: "ScV",
    name: "Sacramento Valley",
    parent: "GV",
    children: [],
    bounds: [-122.5, 38.0, -121.0, 40.5],
    centroid: [-121.8, 39.2],
  },
  SnJV: {
    code: "SnJV",
    name: "San Joaquin Valley",
    parent: "GV",
    children: [],
    bounds: [-121.5, 34.8, -119.5, 38.0],
    centroid: [-120.5, 36.4],
  },
  CW: {
    code: "CW",
    name: "Central Western California",
    parent: "CA-FP",
    children: ["CCo", "SnFrB", "SCoR"],
    bounds: [-123.0, 34.5, -120.0, 38.5],
    centroid: [-121.5, 36.5],
  },
  CCo: {
    code: "CCo",
    name: "Central Coast",
    parent: "CW",
    children: [],
    bounds: [-122.5, 35.0, -120.5, 37.5],
    centroid: [-121.5, 36.2],
  },
  SnFrB: {
    code: "SnFrB",
    name: "San Francisco Bay Area",
    parent: "CW",
    children: [],
    bounds: [-123.0, 37.0, -121.5, 38.5],
    centroid: [-122.2, 37.7],
  },
  SCoR: {
    code: "SCoR",
    name: "South Coast Ranges",
    parent: "CW",
    children: ["SCoRO", "SCoRI"],
    bounds: [-121.5, 34.5, -119.5, 36.5],
    centroid: [-120.5, 35.5],
  },
  SCoRO: {
    code: "SCoRO",
    name: "Outer South Coast Ranges",
    parent: "SCoR",
    children: [],
    bounds: [-121.5, 34.5, -120.5, 36.5],
    centroid: [-121.0, 35.5],
  },
  SCoRI: {
    code: "SCoRI",
    name: "Inner South Coast Ranges",
    parent: "SCoR",
    children: [],
    bounds: [-121.0, 34.5, -119.5, 36.0],
    centroid: [-120.2, 35.2],
  },
  SW: {
    code: "SW",
    name: "Southwestern California",
    parent: "CA-FP",
    children: ["SCo", "ChI", "PR", "TR"],
    bounds: [-120.5, 32.5, -116.0, 35.0],
    centroid: [-118.2, 33.8],
  },
  SCo: {
    code: "SCo",
    name: "South Coast",
    parent: "SW",
    children: [],
    bounds: [-120.5, 33.0, -117.0, 34.5],
    centroid: [-118.5, 33.8],
  },
  ChI: {
    code: "ChI",
    name: "Channel Islands",
    parent: "SW",
    children: ["nChI", "sChI"],
    bounds: [-120.5, 33.0, -118.5, 34.2],
    centroid: [-119.5, 33.6],
  },
  nChI: {
    code: "nChI",
    name: "Northern Channel Islands",
    parent: "ChI",
    children: [],
    bounds: [-120.5, 33.8, -119.0, 34.2],
    centroid: [-119.8, 34.0],
  },
  sChI: {
    code: "sChI",
    name: "Southern Channel Islands",
    parent: "ChI",
    children: [],
    bounds: [-119.5, 33.0, -118.5, 33.5],
    centroid: [-119.0, 33.2],
  },
  PR: {
    code: "PR",
    name: "Peninsular Ranges",
    parent: "SW",
    children: ["SnJt"],
    bounds: [-117.5, 32.5, -116.0, 34.0],
    centroid: [-116.8, 33.2],
  },
  SnJt: {
    code: "SnJt",
    name: "San Jacinto Mountains",
    parent: "PR",
    children: [],
    bounds: [-117.0, 33.5, -116.3, 34.0],
    centroid: [-116.7, 33.8],
  },
  TR: {
    code: "TR",
    name: "Transverse Ranges",
    parent: "SW",
    children: ["WTR", "SnGb", "SnBr"],
    bounds: [-120.0, 34.0, -116.5, 35.0],
    centroid: [-118.2, 34.5],
  },
  WTR: {
    code: "WTR",
    name: "Western Transverse Ranges",
    parent: "TR",
    children: [],
    bounds: [-120.0, 34.0, -118.0, 35.0],
    centroid: [-119.0, 34.5],
  },
  SnGb: {
    code: "SnGb",
    name: "San Gabriel Mountains",
    parent: "TR",
    children: [],
    bounds: [-118.5, 34.1, -117.5, 34.5],
    centroid: [-118.0, 34.3],
  },
  SnBr: {
    code: "SnBr",
    name: "San Bernardino Mountains",
    parent: "TR",
    children: [],
    bounds: [-117.5, 34.0, -116.5, 34.5],
    centroid: [-117.0, 34.2],
  },
  GB: {
    code: "GB",
    name: "Great Basin Province",
    parent: "CA",
    children: ["MP", "SNE"],
    bounds: [-121.5, 36.0, -117.5, 42.0],
    centroid: [-119.5, 39.0],
  },
  MP: {
    code: "MP",
    name: "Modoc Plateau",
    parent: "GB",
    children: ["Wrn"],
    bounds: [-121.5, 40.5, -119.5, 42.0],
    centroid: [-120.5, 41.2],
  },
  Wrn: {
    code: "Wrn",
    name: "Warner Mountains",
    parent: "MP",
    children: [],
    bounds: [-120.5, 41.0, -119.5, 42.0],
    centroid: [-120.0, 41.5],
  },
  SNE: {
    code: "SNE",
    name: "East of Sierra Nevada",
    parent: "GB",
    children: ["W&I"],
    bounds: [-119.5, 36.0, -117.5, 40.0],
    centroid: [-118.5, 38.0],
  },
  "W&I": {
    code: "W&I",
    name: "White and Inyo Mountains",
    parent: "SNE",
    children: [],
    bounds: [-118.5, 36.5, -117.5, 38.0],
    centroid: [-118.0, 37.2],
  },
  D: {
    code: "D",
    name: "Desert Province",
    parent: "CA",
    children: ["DMoj", "DSon"],
    bounds: [-118.0, 32.5, -114.1, 36.5],
    centroid: [-116.0, 34.5],
  },
  DMoj: {
    code: "DMoj",
    name: "Mojave Desert",
    parent: "D",
    children: ["DMtns"],
    bounds: [-118.0, 34.0, -114.5, 36.5],
    centroid: [-116.5, 35.2],
  },
  DMtns: {
    code: "DMtns",
    name: "Desert Mountains",
    parent: "DMoj",
    children: [],
    bounds: [-117.5, 34.5, -115.5, 36.0],
    centroid: [-116.5, 35.2],
  },
  DSon: {
    code: "DSon",
    name: "Sonoran Desert",
    parent: "D",
    children: [],
    bounds: [-116.5, 32.5, -114.1, 34.0],
    centroid: [-115.3, 33.2],
  },
};

// ─── Hierarchy helpers ───

export function getDescendants(code: string): string[] {
  const region = JEPSON_REGIONS[code];
  if (!region) return [];
  const result: string[] = [];
  for (const child of region.children) {
    result.push(child);
    result.push(...getDescendants(child));
  }
  return result;
}

export function getAncestors(code: string): string[] {
  const result: string[] = [];
  let current = JEPSON_REGIONS[code];
  while (current?.parent) {
    result.push(current.parent);
    current = JEPSON_REGIONS[current.parent];
  }
  return result;
}

export function expandRegion(code: string): string[] {
  return [code, ...getDescendants(code)];
}

// ─── bioregional_distributions Parser ───

export function parseBioregions(raw: string): Set<string> {
  if (!raw || raw.trim() === "") return new Set();

  let cleaned = raw
    .replace(/&amp;/g, "&")
    .replace(/\.$/, "")
    .trim();

  const result = new Set<string>();
  const segments = splitTopLevel(cleaned);

  for (const seg of segments) {
    const trimmed = seg.trim();
    if (!trimmed) continue;
    parseSegment(trimmed, result);
  }

  return result;
}

function splitTopLevel(str: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";

  for (const ch of str) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;

    if (ch === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current) parts.push(current);
  return parts;
}

function parseSegment(segment: string, result: Set<string>) {
  let text = segment.trim();

  let excCodes: string[] = [];
  const excMatch = text.match(/\(exc\s+([^)]+)\)/i);
  if (excMatch) {
    excCodes = excMatch[1]
      .split(/[,&]/)
      .map((s) => s.trim())
      .filter(Boolean);
    text = text.replace(/\(exc\s+[^)]+\)/i, "").trim();
  }

  const excInlineMatch = text.match(/^(.+?)\s+exc\s+(.+)$/i);
  if (excInlineMatch) {
    text = excInlineMatch[1].trim();
    excCodes.push(
      ...excInlineMatch[2]
        .split(/[,&]/)
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }

  text = text.replace(/\([^)]*\)/g, "").trim();
  text = text.replace(/^[nscwe](?:&[nscwe])?\s+/i, "").trim();

  const code = text;

  if (!JEPSON_REGIONS[code]) {
    return;
  }

  const expanded = expandRegion(code);

  const excludeSet = new Set<string>();
  for (const exc of excCodes) {
    const cleanExc = exc.replace(/^[nscwe](?:&[nscwe])?\s+/i, "").trim();
    if (JEPSON_REGIONS[cleanExc]) {
      expandRegion(cleanExc).forEach((c) => excludeSet.add(c));
    }
  }

  for (const c of expanded) {
    if (!excludeSet.has(c)) {
      result.add(c);
    }
  }
}

// ─── GeoJSON Code Mapping ───

const GEOJSON_CODE_MAP: Record<string, string> = {
  "c SNF": "cSNF",
  "c SNH": "cSNH",
  "n SNF": "nSNF",
  "n SNH": "nSNH",
  "s SNF": "sSNF",
  "s SNH": "sSNH",
  "n Chi": "nChI",
  "s Chi": "sChI",
};

const GEOJSON_NAME_MAP: Record<string, string> = {
  "Peninsular Ranges": "PR",
};

export function normalizeGeoJSONCode(
  subregion: string,
  name?: string
): string {
  if (name && GEOJSON_NAME_MAP[name]) return GEOJSON_NAME_MAP[name];
  if (GEOJSON_CODE_MAP[subregion]) return GEOJSON_CODE_MAP[subregion];
  return subregion;
}

// ─── Polygon → Region Matching ───

interface GeoJSONFeature {
  type: "Feature";
  geometry: any;
  properties: {
    region?: string;
    subregion?: string;
    name?: string;
    [key: string]: any;
  };
}

let cachedBioregionGeoJSON: GeoJSONFeature[] | null = null;

export async function loadBioregionGeoJSON(): Promise<GeoJSONFeature[]> {
  // The polygon file is Jepson's. It was moved out of public/ on 2026-09-22 so it
  // is no longer served from our domain at all (assets-withheld/ is outside the
  // build), and this returns nothing while the data is hidden. Restoring means
  // moving the file back into public/ and flipping the flag.
  if (!JEPSON_DATA_ENABLED) return [];
  if (cachedBioregionGeoJSON) return cachedBioregionGeoJSON;
  try {
    const res = await fetch("/jepson-bioregions.geojson");
    if (!res.ok) return [];
    const data = await res.json();
    cachedBioregionGeoJSON = data.features || [];
    return cachedBioregionGeoJSON!;
  } catch {
    return [];
  }
}

export async function matchPolygonToRegionsAsync(
  polygon: any
): Promise<string[]> {
  const features = await loadBioregionGeoJSON();
  if (features.length > 0) {
    return matchPolygonWithGeoJSON(polygon, features);
  }
  return matchPolygonToRegions(polygon);
}

function matchPolygonWithGeoJSON(
  polygon: any,
  features: GeoJSONFeature[]
): string[] {
  const turfPoly = turf.polygon(polygon.coordinates);
  const matched = new Set<string>();

  for (const feat of features) {
    try {
      const regionPoly =
        feat.geometry.type === "Polygon"
          ? turf.polygon(feat.geometry.coordinates)
          : feat.geometry.type === "MultiPolygon"
          ? turf.multiPolygon(feat.geometry.coordinates)
          : null;

      if (!regionPoly) continue;

      if (turf.booleanIntersects(turfPoly, regionPoly)) {
        const code = normalizeGeoJSONCode(
          feat.properties.subregion || "",
          feat.properties.name
        );
        if (code && JEPSON_REGIONS[code]) {
          matched.add(code);
        }
      }
    } catch {
      // Skip features with invalid geometry
    }
  }

  return Array.from(matched);
}

export function matchPolygonToRegions(polygon: any): string[] {
  const turfPoly = turf.polygon(polygon.coordinates);
  const polyBbox = turf.bbox(turfPoly);
  const matched: string[] = [];

  for (const [code, region] of Object.entries(JEPSON_REGIONS)) {
    if (code === "CA") continue;

    const [rMinLng, rMinLat, rMaxLng, rMaxLat] = region.bounds;
    if (
      rMaxLng < polyBbox[0] ||
      rMinLng > polyBbox[2] ||
      rMaxLat < polyBbox[1] ||
      rMinLat > polyBbox[3]
    ) {
      continue;
    }

    const centroidPoint = turf.point(region.centroid);
    if (turf.booleanPointInPolygon(centroidPoint, turfPoly)) {
      matched.push(code);
      continue;
    }

    const polyCenter = turf.center(turfPoly);
    const [pcLng, pcLat] = polyCenter.geometry.coordinates;
    if (
      pcLng >= rMinLng &&
      pcLng <= rMaxLng &&
      pcLat >= rMinLat &&
      pcLat <= rMaxLat
    ) {
      matched.push(code);
    }
  }

  return matched;
}

// ─── Range Status Checking ───

/** Check if a species is out of range for the given survey regions */
export function isOutOfRange(
  speciesBioregions: string | null | undefined,
  surveyRegions: string[]
): boolean {
  if (!speciesBioregions || !speciesBioregions.trim()) return false; // unknown = don't flag
  if (surveyRegions.length === 0) return false;

  const speciesRegions = parseBioregions(speciesBioregions);
  if (speciesRegions.size === 0) return false;

  for (const target of surveyRegions) {
    // Direct match
    if (speciesRegions.has(target)) return false;
    // Ancestor match (e.g., species says "SN" and target is "cSNF")
    for (const anc of getAncestors(target)) {
      if (speciesRegions.has(anc)) return false;
    }
  }
  return true; // no overlap found
}

/** Get range status label for display */
export function getRangeStatus(
  speciesBioregions: string | null | undefined,
  surveyRegions: string[]
): "in-range" | "out-of-range" | "unknown" {
  if (!speciesBioregions || !speciesBioregions.trim()) return "unknown";
  if (surveyRegions.length === 0) return "unknown";
  return isOutOfRange(speciesBioregions, surveyRegions)
    ? "out-of-range"
    : "in-range";
}

// ─── Region color mapping for map overlay ───

export const REGION_COLORS: Record<string, string> = {
  NW: "#52c41a",   // green
  CaR: "#722ed1",  // purple
  SN: "#1890ff",   // blue
  GV: "#faad14",   // gold
  CW: "#13c2c2",   // cyan
  SW: "#eb2f96",   // pink
  GB: "#fa8c16",   // orange
  D: "#a0522d",    // brown
};

export function getRegionColor(code: string): string {
  // Walk up to find the major region parent
  let current = JEPSON_REGIONS[code];
  while (current) {
    if (REGION_COLORS[current.code]) return REGION_COLORS[current.code];
    if (!current.parent) break;
    current = JEPSON_REGIONS[current.parent];
  }
  return "#8c8c8c"; // default gray
}
